import { Component, OnInit, ViewEncapsulation, inject } from '@angular/core';
import { UsersService } from 'src/app/services/users/users.service';
import { UserInfo } from '../../models/user-info.model';
import { MatTabChangeEvent } from '@angular/material/tabs';
import { entAnimations } from 'src/app/animations';
import { DISPLAYMODE, UserSettings } from 'src/app/models/user-settings.model';
import { catchError, of, switchMap } from 'rxjs';
import { SettingsService } from 'src/app/services/settings/settings.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { SuccesSnackbarComponent } from '../snackbars/succes-snackbar/success-snackbar.component';
import { TranslateService } from '@ngx-translate/core';
import { ErrorSnackbarComponent } from '../snackbars/error-snackbar/error-snackbar.component';

@Component({
  selector: 'app-settings',
  templateUrl: './settings.component.html',
  styleUrls: ['./settings.component.scss'],
  encapsulation: ViewEncapsulation.None,
  animations: entAnimations,
  standalone: false,
})
export class SettingsComponent implements OnInit {
  private _settingsService = inject(SettingsService);
  private _translateService = inject(TranslateService);
  private _userService = inject(UsersService);
  private _snackBar = inject(MatSnackBar);

  // API Calls
  isLoading = false;
  currentUser: UserInfo = new UserInfo();
  hasMultiEtab = false;

  private userSettings: UserSettings;
  newAffichageMode: number;
  newEtablissement: string;

  saveDisabled = true;

  // Tabs
  selectedIndex = 0;

  ngOnInit() {
    this.currentUser = this._userService.currentUserValue;
    if (this.currentUser.groupEtablissement?.length > 1) {
      this.hasMultiEtab = true;
    }
    this.getUserSettings();
  }

  private getUserSettings(): void {
    this.isLoading = true;

    this._settingsService
      .getUserSettings(this.currentUser.oid)
      .pipe(
        switchMap(userSettings => {
          if (userSettings !== null) {
            return of(userSettings);
          } else {
            const newUserSettings: UserSettings = {
              defaultGE: this.currentUser.groupEtablissement[0],
              displayModeId: DISPLAYMODE.Maxi,
            };
            return this._settingsService.createUserSettings(newUserSettings);
          }
        }),
        catchError(() => of(null))
      )
      .subscribe({
        next: userSettings => {
          if (userSettings) {
            this.userSettings = userSettings;
            this.newAffichageMode = userSettings.displayModeId;
            this.newEtablissement = userSettings.defaultGE;
          }
          this.isLoading = false;
        },
        error: () => (this.isLoading = false),
      });
  }

  tabChanged(tabChangeEvent: MatTabChangeEvent): void {
    this.selectedIndex = tabChangeEvent.index;
  }

  onDisplayModeChange(affichage: number) {
    this.newAffichageMode = affichage;
    this.isSaveDisabled();
  }

  isSaveDisabled() {
    if (
      this.newAffichageMode !== this.userSettings.displayModeId ||
      this.newEtablissement !== this.userSettings.defaultGE
    ) {
      this.saveDisabled = false;
    } else {
      this.saveDisabled = true;
    }
  }

  saveSettings(): void {
    const newSettings: UserSettings = {
      displayModeId: this.newAffichageMode,
      defaultGE: this.newEtablissement,
    };
    this._settingsService.updateUserSettings(newSettings).subscribe({
      next: us => {
        this.userSettings = us;
        this.isSaveDisabled();
        this._snackBar.openFromComponent(SuccesSnackbarComponent, {
          data: this._translateService.instant('USERS.SETTINGS.TABS1.SAVED'),
          duration: 5000,
          horizontalPosition: 'right',
          verticalPosition: 'bottom',
        });
      },
      error: () => {
        this._snackBar.openFromComponent(ErrorSnackbarComponent, {
          data: this._translateService.instant('USERS.SETTINGS.TABS1.ERROR'),
          horizontalPosition: 'right',
          verticalPosition: 'bottom',
        });
      },
    });
  }
}
